import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Download, Copy, Trash2, Check, RotateCcw, ChevronRight, ChevronDown } from 'lucide-react';
import { brioGridTheme } from '../agGridTheme';
import { effectiveUnit } from '../utils/measureUnits';
import { formatMoney, formatQty } from '../utils/money';
import { ToolbarIcon } from './ToolbarIcon';
import BoqGridHeader from './BoqGridHeader';
import { useBoqGridLayout, COLLAPSED_W, QS_ROW_SELECTION, QS_SELECTION_COL_DEF } from '../hooks/useBoqGridLayout';
import { StickyScrollbar } from './StickyScrollbar';

// Shared QS grid layout config (collapse / freeze / persisted widths + order).
const PI_LAYOUT_KEY = 'qs-items-layout-v1';
const PI_COL_W = {
  fullCode: 140, subtrade: 150, optionGroup: 180, shortDescription: 320, unit: 90,
  quantity: 120, measureBasis: 120, takeoff: 120, rate: 130,
  supplyRateQuoted: 120, installRateQuoted: 120, supplyRateBilled: 120, installRateBilled: 120,
  amount: 150, rateRef: 90, projectZoneId: 150, scopePartyId: 150,
};
// Code is always frozen; everything else can collapse / freeze.
const PI_STATIC_COL_IDS = new Set(['fullCode']);
const PI_TOGGLEABLE_COL_IDS = [
  'subtrade', 'optionGroup', 'shortDescription', 'unit', 'quantity', 'measureBasis', 'takeoff',
  'rate', 'supplyRateQuoted', 'installRateQuoted', 'supplyRateBilled', 'installRateBilled',
  'amount', 'rateRef', 'projectZoneId', 'scopePartyId',
];

// Resizable table body. The BOQ page only affords this card ~half the viewport,
// so the grid gets a draggable bottom edge; the chosen height is remembered per
// browser (double-click the handle to reset to the default).
const PI_HEIGHT_KEY = 'qs-items-grid-height-v1';
const PI_MIN_HEIGHT = 200;
const PI_MAX_HEIGHT = 2400;
const clampGridHeight = (h) => Math.max(PI_MIN_HEIGHT, Math.min(PI_MAX_HEIGHT, Math.round(h)));

// Flatten the zone tree into [{ id, path }] for the zone cell editor.
function flattenZones(tree, depth = 0, acc = []) {
  tree.forEach((z) => {
    acc.push({ id: z.id, path: `${'— '.repeat(depth)}${z.code} · ${z.label}` });
    if (z.children?.length) flattenZones(z.children, depth + 1, acc);
  });
  return acc;
}

// Excel often gives back numbers with thousands separators and currency
// symbols; strip them when pasting into numeric cells.
function parsePastedNumber(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const cleaned = String(raw).replace(/[,$€£₹\s]/g, '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
}

/**
 * Spreadsheet-style grid for project_items with full Excel-style UX
 * (Community-tier).
 *
 * Enabled:
 *   - cell range selection (click + drag, shift + arrow)
 *   - copy (Ctrl/Cmd+C), paste (Ctrl/Cmd+V) — both into the grid and from
 *     external sources like Excel or Google Sheets
 *   - undo/redo for cell edits (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z)
 *   - cell change flash on commit
 *   - CSV export
 *   - quick filter (search across all visible cells)
 *
 * Not enabled (AG Grid Enterprise-only):
 *   - fill handle (drag the corner of a selection to fill cells)
 *   - Excel export
 *   - rich right-click context menu
 */
const BASIS_LABEL = {
  '': 'Manual',
  count: 'Count',
  running_m: 'Running m',
  plan_area: 'Plan m²',
  elevation_area: 'Elev m²',
  volume: 'Volume',
  carpet_area: 'Carpet m²',
  built_up_area: 'Built-up m²',
  construction_area: 'Constr m²',
};
// Editable take-off bases (zone gross-area bases are display-only, used by
// SD allowance lines, so they're intentionally not offered here).
const BASIS_VALUES = ['', 'count', 'running_m', 'plan_area', 'elevation_area', 'volume'];
const LINE_TYPE_TAG = { provisional_sum: 'PROV', pc_sum: 'PC', dayworks: 'DAYW', allowance: 'ALLOW' };
// Lump-sum line types have no measure basis (show "—" in the Basis column);
// allowance lines DO carry a basis, so they're excluded here.
const isLumpType = (lt) => lt === 'provisional_sum' || lt === 'pc_sum' || lt === 'dayworks';

// Estimate-stage pricing-basis colour key, applied to the Code column only.
// Every BOQ line is one of two kinds:
//   • library  — priced off a library item_version: a real material / take-off
//                calculation (line_type 'measured').
//   • sd       — an SD-level block estimate: an area-rate allowance (priced per
//                floor area) or a provisional / PC / daywork lump sum.
// Their code cells get a faint, non-semantic fill (neutral grey vs brand gold
// tint — neither signals right/wrong) so it's clear at a glance which figures
// are measured from materials and which are budgetary block estimates.
const lineKindOf = (lineType) => (lineType && lineType !== 'measured' ? 'sd' : 'library');
const LINE_KIND = {
  library: {
    label: 'Library item — material calc',
    bg: 'var(--brio-grey-light, #f5f5f5)',
  },
  sd: {
    label: 'SD block estimate — lump / area rate',
    bg: 'var(--brio-gold-light, rgba(159, 144, 42, 0.1))',
  },
};

function MeasureCellRenderer(p) {
  // Lump-sum lines (prov sum / PC / dayworks) carry a single figure — no take-off.
  if (isLumpType(p.data.lineType)) return <span style={{ color: 'var(--brio-text-muted)' }}>—</span>;
  const n = p.data.measurementCount || 0;
  // Allowance lines (e.g. the seeded SD template placeholders) carry no take-off
  // by default; show a dashed "+ Take-off" to start one. They keep their
  // line_type — a take-off just derives the line's quantity from a few
  // measurements, so an SD allowance can be quantified in-app instead of Excel.
  const allowanceStart = p.data.lineType === 'allowance' && n === 0;
  return (
    <button
      type="button"
      onClick={() => p.context.onMeasure && p.context.onMeasure(p.data.id)}
      title={allowanceStart ? 'Start a take-off — derive this line’s quantity from a few measurements' : undefined}
      style={{
        fontSize: '0.74rem', padding: '0.15rem 0.5rem',
        border: `1px ${allowanceStart ? 'dashed' : 'solid'} var(--brio-border, #ccc)`, borderRadius: 4,
        background: n ? 'var(--brio-gold-faint, #f7f1d4)' : 'transparent', cursor: 'pointer',
        color: allowanceStart ? 'var(--brio-text-muted)' : undefined,
      }}
    >
      {n ? `Take-off (${n})` : (allowanceStart ? '+ Take-off' : 'Take-off')}
    </button>
  );
}

function RatesCellRenderer(p) {
  return (
    <button
      type="button"
      onClick={() => p.context.onRates && p.context.onRates(p.data.id)}
      style={{
        fontSize: '0.74rem', padding: '0.15rem 0.5rem',
        border: '1px solid var(--brio-border, #ccc)', borderRadius: 4,
        background: 'transparent', cursor: 'pointer',
      }}
    >
      Rates
    </button>
  );
}

// Renders the option-group cell: empty for non-option lines, a "Selected"
// badge for the chosen alternate, or a "Use this option" button for an
// unselected alternate. The group label is always shown above.
function OptionCellRenderer(p) {
  const group = p.data.optionGroup;
  if (!group) return null;
  const selected = !!p.data.isSelected;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.15, padding: '2px 0' }}>
      <span style={{ fontSize: '0.7rem', color: 'var(--brio-text-muted, #777)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={group}>
        {group}
      </span>
      {selected ? (
        <span style={{
          fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: 3,
          color: 'var(--brio-gold, #9F902A)', fontWeight: 600,
        }}>
          <Check size={11} /> Selected
        </span>
      ) : (
        <button
          type="button"
          onClick={() => p.context.onSelectOption && p.context.onSelectOption(p.data.id)}
          style={{
            fontSize: '0.7rem', padding: '0.1rem 0.4rem', alignSelf: 'flex-start',
            border: '1px solid var(--brio-border, #ccc)', borderRadius: 3,
            background: 'transparent', cursor: 'pointer', color: 'var(--brio-text, #333)',
          }}
        >
          Use this option
        </button>
      )}
    </div>
  );
}

// Full-width banner row that heads each trade section in the grouped view —
// mirrors the Summary's section rows (chapter label + estimate / tender
// subtotal). It's a collapse / expand drawer header: clicking it toggles the
// section open or shut. Rendered for rows flagged with `__group`.
function TradeGroupRowRenderer(p) {
  const { key, label, quoted, billed, count, isEmpty, collapsed } = p.data;
  const cur = p.context.currency || 'INR';
  const toggle = () => { if (!isEmpty && p.context.onToggleCollapse) p.context.onToggleCollapse(key); };
  const Chevron = collapsed ? ChevronRight : ChevronDown;
  return (
    <div
      onClick={toggle}
      role="button"
      tabIndex={isEmpty ? -1 : 0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isEmpty) { e.preventDefault(); toggle(); } }}
      title={isEmpty ? undefined : (collapsed ? 'Expand trade' : 'Collapse trade')}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
        width: '100%', height: '100%', padding: '0 0.6rem', boxSizing: 'border-box',
        fontFamily: 'Raleway, sans-serif', fontWeight: 700,
        cursor: isEmpty ? 'default' : 'pointer',
        color: isEmpty ? 'var(--brio-text-muted)' : 'var(--brio-text, #333)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Chevron size={15} style={{ flexShrink: 0, opacity: isEmpty ? 0.35 : 0.8 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
          <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--brio-text-muted)', marginLeft: 8 }}>
            {isEmpty ? 'no lines yet' : `${count} line${count === 1 ? '' : 's'}`}
          </span>
        </span>
      </span>
      {!isEmpty && (
        <span style={{ display: 'inline-flex', gap: '1.25rem', fontSize: '0.82rem', fontWeight: 600, flexShrink: 0 }}>
          <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400 }}>
            Estimate <span style={{ color: 'var(--brio-text, #333)', fontWeight: 700, marginLeft: 4 }}>{formatMoney(quoted, cur)}</span>
          </span>
          <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400 }}>
            Tender <span style={{ color: 'var(--brio-text, #333)', fontWeight: 700, marginLeft: 4 }}>{formatMoney(billed, cur)}</span>
          </span>
        </span>
      )}
    </div>
  );
}

// Sub-trade band — a lighter, indented drawer header nested under a chapter
// section, mirroring the Summary's sub-trade rows. Collapsible like the chapter
// header but visually subordinate (no gold band, smaller type).
function SubtradeBandRowRenderer(p) {
  const { key, label, quoted, billed, count, collapsed } = p.data;
  const cur = p.context.currency || 'INR';
  const toggle = () => { if (p.context.onToggleCollapse) p.context.onToggleCollapse(key); };
  const Chevron = collapsed ? ChevronRight : ChevronDown;
  return (
    <div
      onClick={toggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
      title={collapsed ? 'Expand sub-trade' : 'Collapse sub-trade'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
        width: '100%', height: '100%', padding: '0 0.6rem 0 1.6rem', boxSizing: 'border-box',
        fontFamily: 'Raleway, sans-serif', fontWeight: 600, cursor: 'pointer',
        color: 'var(--brio-text, #333)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Chevron size={13} style={{ flexShrink: 0, opacity: 0.7 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.88rem' }}>
          {label}
          <span style={{ fontWeight: 400, fontSize: '0.74rem', color: 'var(--brio-text-muted)', marginLeft: 8 }}>
            {`${count} line${count === 1 ? '' : 's'}`}
          </span>
        </span>
      </span>
      <span style={{ display: 'inline-flex', gap: '1.25rem', fontSize: '0.78rem', flexShrink: 0 }}>
        <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400 }}>
          Estimate <span style={{ color: 'var(--brio-text, #333)', fontWeight: 600, marginLeft: 4 }}>{formatMoney(quoted, cur)}</span>
        </span>
        <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400 }}>
          Tender <span style={{ color: 'var(--brio-text, #333)', fontWeight: 600, marginLeft: 4 }}>{formatMoney(billed, cur)}</span>
        </span>
      </span>
    </div>
  );
}

// Dispatcher for AG Grid's single full-width renderer slot: chapter headers vs
// sub-trade bands.
function GroupBandRowRenderer(p) {
  return p.data?.__subgroup ? <SubtradeBandRowRenderer {...p} /> : <TradeGroupRowRenderer {...p} />;
}

// Group rows into ordered trade sections (chapters). Mirrors SummaryTab's
// buildTradeRows: SD lines carry their canonical `trade` label directly;
// measured/tender library lines roll up to their canonical chapter via
// `chapterByItem` (resolved in useBoqLibrary from whichever taxonomy the item is
// classified in). Anything that can't be rolled up falls back to its trade_group
// label, then its raw trade, then the code prefix. When `canonicalTrades` is
// supplied every chapter is rendered (numbered 1..N) even with no lines, so the
// QS sees the whole catalogue. Returns sections with their items; the component
// flattens them into rows, inserting collapse drawers.
function buildTradeSections(rows, canonicalTrades, chapterByItem, tradeGroupByItem) {
  const MAX = Number.MAX_SAFE_INTEGER;
  const sections = new Map();
  const canonByLabel = new Map();
  if (canonicalTrades && canonicalTrades.length > 0) {
    canonicalTrades.forEach((t, i) => {
      const key = `t:${t.label}`;
      canonByLabel.set(t.label, i + 1);
      sections.set(key, { key, label: t.label, sort: i + 1, canonNum: i + 1, quoted: 0, billed: 0, items: [] });
    });
  }

  rows.forEach((r) => {
    let secKey; let secLabel; let secSort;
    const tg = tradeGroupByItem ? tradeGroupByItem.get(r.itemCode) : null;
    // Canonical chapter: SD lines store it directly; library lines resolve via
    // chapterByItem (either taxonomy). Roll everything up so the bill organises
    // like SD regardless of how the line was tagged.
    const chapter = (r.trade && canonByLabel.has(r.trade))
      ? r.trade
      : (chapterByItem ? chapterByItem.get(r.itemCode) : null);
    if (chapter) {
      secLabel = chapter; secKey = `t:${chapter}`;
      const num = canonByLabel.get(chapter);
      secSort = num != null ? num : (r.sequenceOrder ?? MAX);
    } else if (tg) {
      // Classified but in a group with no chapter (e.g. Miscellaneous) — show the
      // friendly trade_group label rather than the raw 2-letter code.
      secKey = `g:${tg.code}`; secLabel = tg.label; secSort = tg.sortOrder ?? MAX;
    } else if (r.trade) {
      secLabel = r.trade; secKey = `t:${secLabel}`;
      const num = canonByLabel.get(secLabel);
      secSort = num != null ? num : (r.sequenceOrder ?? MAX);
    } else {
      const prefix = (r.fullCode || '').slice(0, 2) || 'Other';
      secKey = `p:${prefix}`; secLabel = prefix; secSort = MAX;
    }
    if (!sections.has(secKey)) sections.set(secKey, { key: secKey, label: secLabel, sort: secSort, quoted: 0, billed: 0, items: [] });
    const sec = sections.get(secKey);
    sec.items.push(r);
    if (r.isSelected) { sec.quoted += r.amountQuoted || 0; sec.billed += r.amountBilled || 0; }
    if (secSort < sec.sort) sec.sort = secSort;
  });

  const bySort = (a, b) => (a.sort !== b.sort ? a.sort - b.sort : a.label.localeCompare(b.label));
  return [...sections.values()].sort(bySort).map((sec) => {
    // Within each chapter, band the lines by their (canonical) sub-trade so the
    // bill reads chapter -> sub-trade -> line, exactly like the Summary. Order
    // bands by the earliest line they contain so they follow the bill sequence.
    const subMap = new Map();
    sec.items.forEach((it) => {
      const label = it.subtrade || '';
      if (!subMap.has(label)) subMap.set(label, { key: label, label, sort: it.sequenceOrder ?? MAX, quoted: 0, billed: 0, items: [] });
      const sg = subMap.get(label);
      sg.items.push(it);
      if (it.isSelected) { sg.quoted += it.amountQuoted || 0; sg.billed += it.amountBilled || 0; }
      if ((it.sequenceOrder ?? MAX) < sg.sort) sg.sort = it.sequenceOrder ?? MAX;
    });
    const subgroups = [...subMap.values()].sort(bySort);
    // Only band when there's real sub-trade structure to show — a lone bucket
    // that's unlabelled (or just repeats the chapter name) gets no band.
    const hasBands = subgroups.length > 1
      || (subgroups.length === 1 && !!subgroups[0].label && subgroups[0].label !== sec.label);
    return {
      key: sec.key,
      label: sec.canonNum ? `${sec.canonNum}. ${sec.label}` : sec.label,
      quoted: sec.quoted,
      billed: sec.billed,
      items: sec.items,
      subgroups,
      hasBands,
    };
  });
}

function ProjectItemsGrid({
  items,
  zoneTree,
  scopeParties = [],
  canonicalTrades = null,
  tradeGroupByItem = null,
  chapterByItem = null,
  subtradeByItem = null,
  onUpdate,
  onDelete,
  onDuplicate,
  onMeasure,
  onRates,
  onSelectOption,
  onBeforeBulk,
  onBeforeDuplicate,
  currency = 'INR',
  billView = 'quoted',
  onBillViewChange,
  usesCostSplit = false,
  onCollapseControls,
  height = 480,
}) {
  const gridRef = useRef(null);
  const gridBoxRef = useRef(null);

  // Draggable table height (persisted per browser). Initialised from storage,
  // falling back to the `height` prop; a bottom handle drives setGridHeight.
  const [gridHeight, setGridHeight] = React.useState(() => {
    try {
      const saved = Number(window.localStorage.getItem(PI_HEIGHT_KEY));
      if (Number.isFinite(saved) && saved > 0) return clampGridHeight(saved);
    } catch { /* storage may be unavailable (e.g. private mode) */ }
    return height;
  });
  // Live mirror of the height so drag/cleanup closures read the latest value
  // without re-subscribing listeners on every pixel of movement.
  const heightRef = useRef(gridHeight);
  heightRef.current = gridHeight;
  const dragAbortRef = useRef(null);

  const onResizeStart = useCallback((e) => {
    if (e.button !== 0) return; // primary button / touch / pen only
    e.preventDefault();
    const startY = e.clientY;
    const startH = heightRef.current;
    const controller = new AbortController();
    dragAbortRef.current = controller;
    const { signal } = controller;
    window.addEventListener('pointermove', (ev) => {
      setGridHeight(clampGridHeight(startH + (ev.clientY - startY)));
    }, { signal });
    const end = () => {
      controller.abort(); // detaches every listener bound to this signal
      dragAbortRef.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      try { window.localStorage.setItem(PI_HEIGHT_KEY, String(heightRef.current)); } catch { /* ignore */ }
    };
    window.addEventListener('pointerup', end, { signal });
    window.addEventListener('pointercancel', end, { signal });
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
  }, []);

  const resetGridHeight = useCallback(() => {
    setGridHeight(height);
    try { window.localStorage.removeItem(PI_HEIGHT_KEY); } catch { /* ignore */ }
  }, [height]);

  // Abort an in-flight drag if we unmount mid-gesture (avoids a leaked listener).
  useEffect(() => () => { if (dragAbortRef.current) dragAbortRef.current.abort(); }, []);

  // Shared QS grid layout: collapse, freeze, persisted widths/order, checkbox.
  const {
    gridContext: layoutContext, decorateColumns, applySavedOrder, onColumnResized, onColumnMoved, resetLayout,
  } = useBoqGridLayout({
    storageKey: PI_LAYOUT_KEY, defaultWidths: PI_COL_W,
    toggleableColIds: PI_TOGGLEABLE_COL_IDS, staticColIds: PI_STATIC_COL_IDS, gridRef,
  });
  const [quickFilter, setQuickFilter] = React.useState('');
  // Group rows into trade sections (like the Summary). On by default; switch off
  // for flat, fully-sortable editing (e.g. heavy Excel paste across trades).
  const [groupByTrade, setGroupByTrade] = React.useState(true);
  // Collapsed trade sections (by section key) — each section header is a drawer.
  const [collapsedKeys, setCollapsedKeys] = React.useState(() => new Set());
  const toggleCollapse = useCallback((key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const gridContext = useMemo(
    () => ({ ...layoutContext, onMeasure, onRates, onSelectOption, currency, onToggleCollapse: toggleCollapse }),
    [layoutContext, onMeasure, onRates, onSelectOption, currency, toggleCollapse]
  );
  const setBillView = onBillViewChange || (() => {});
  const rateField = billView === 'billed' ? 'rateBilled' : 'rateQuoted';
  const amountField = billView === 'billed' ? 'amountBilled' : 'amountQuoted';

  // The supply/installation breakdown only exists on the estimate basis and is
  // only shown for projects that bill the split. Defaults to the project flag
  // but can be toggled per view.
  const [showSplit, setShowSplit] = React.useState(!!usesCostSplit);
  React.useEffect(() => { setShowSplit(!!usesCostSplit); }, [usesCostSplit]);
  // The split applies to whichever basis is in view (estimate or tender).
  const splitActive = showSplit;

  const scopeIds = useMemo(() => [null, ...scopeParties.map((p) => p.id)], [scopeParties]);
  const scopeNameById = useMemo(() => {
    const m = new Map();
    scopeParties.forEach((p) => m.set(p.id, p.isExternal ? `${p.name} ↗` : p.name));
    return m;
  }, [scopeParties]);

  const zoneOptions = useMemo(() => flattenZones(zoneTree || []), [zoneTree]);
  const zoneIds = useMemo(() => [null, ...zoneOptions.map((z) => z.id)], [zoneOptions]);
  const zoneLabelById = useMemo(() => {
    const m = new Map();
    zoneOptions.forEach((z) => m.set(z.id, z.path));
    return m;
  }, [zoneOptions]);
  const zoneIdByLabel = useMemo(() => {
    const m = new Map();
    zoneOptions.forEach((z) => m.set(z.path.replace(/^[—\s]+/, ''), z.id));
    zoneOptions.forEach((z) => m.set(z.path, z.id));
    return m;
  }, [zoneOptions]);

  // Flatten row shape so AG Grid can read nested item_version fields.
  const rowData = useMemo(() => items.map((it) => ({
    id: it.id,
    fullCode: it.version?.fullCode || LINE_TYPE_TAG[it.lineType] || '—',
    shortDescription: it.descriptionOverride || it.version?.shortDescription || '',
    trade: it.trade || '',
    // Prefer the canonical brio sub-trade (so library/tender lines read the same
    // as SD); fall back to the line's own sub-trade label.
    subtrade: (subtradeByItem && subtradeByItem.get(it.version?.itemCode)) || it.subtrade || '',
    itemCode: it.version?.itemCode || null,
    unit: effectiveUnit(it),
    quantity: Number(it.quantity),
    projectZoneId: it.projectZoneId,
    descriptionOverride: it.descriptionOverride,
    versionDescription: it.version?.shortDescription || '',
    sequenceOrder: it.sequenceOrder,
    measureBasis: it.measureBasis || '',
    measurementCount: it.measurementCount || 0,
    lineType: it.lineType || 'measured',
    kind: lineKindOf(it.lineType),
    scopePartyId: it.scopePartyId || null,
    rateQuoted: it.rateQuoted == null ? null : Number(it.rateQuoted),
    rateBilled: it.rateBilled == null ? null : Number(it.rateBilled),
    supplyRateQuoted: it.supplyRateQuoted == null ? null : Number(it.supplyRateQuoted),
    installRateQuoted: it.installRateQuoted == null ? null : Number(it.installRateQuoted),
    supplyRateBilled: it.supplyRateBilled == null ? null : Number(it.supplyRateBilled),
    installRateBilled: it.installRateBilled == null ? null : Number(it.installRateBilled),
    amountQuoted: it.amountQuoted == null ? null : Number(it.amountQuoted),
    amountBilled: it.amountBilled == null ? null : Number(it.amountBilled),
    optionGroup: it.optionGroup || null,
    isSelected: it.isSelected !== false,
  })), [items, subtradeByItem]);

  // Totals exclude alternates that aren't the chosen option.
  const totalQuoted = useMemo(
    () => rowData.reduce((a, r) => a + (r.isSelected ? (r.amountQuoted || 0) : 0), 0),
    [rowData]);
  const totalBilled = useMemo(
    () => rowData.reduce((a, r) => a + (r.isSelected ? (r.amountBilled || 0) : 0), 0),
    [rowData]);
  const alternateQuoted = useMemo(
    () => rowData.reduce((a, r) => a + ((!r.isSelected && r.optionGroup) ? (r.amountQuoted || 0) : 0), 0),
    [rowData]);
  // Only show the Sub-trade column when some line carries one (SD allowance bills).
  const hasSubtrade = useMemo(() => rowData.some((r) => r.subtrade), [rowData]);
  // Only show the Option column when at least one line is in an option group.
  const hasOptions = useMemo(() => rowData.some((r) => r.optionGroup), [rowData]);
  // Only surface a pricing-basis key for the kinds actually present in the bill.
  const hasLibrary = useMemo(() => rowData.some((r) => r.kind === 'library'), [rowData]);
  const hasSd = useMemo(() => rowData.some((r) => r.kind === 'sd'), [rowData]);

  // Grouping only applies when not searching — a quick filter falls back to the
  // flat list so the search hits aren't hidden under (now mismatched) headers.
  const effectiveGrouped = groupByTrade && !quickFilter;
  const sections = useMemo(
    () => (effectiveGrouped ? buildTradeSections(rowData, canonicalTrades, chapterByItem, tradeGroupByItem) : null),
    [effectiveGrouped, rowData, canonicalTrades, chapterByItem, tradeGroupByItem]
  );
  // Flatten sections into rows, inserting a drawer header per trade and hiding
  // the lines of collapsed sections.
  const displayRows = useMemo(() => {
    if (!sections) return rowData;
    const out = [];
    sections.forEach((sec) => {
      const collapsed = collapsedKeys.has(sec.key);
      out.push({
        __group: true, id: `__group__${sec.key}`, key: sec.key, label: sec.label,
        quoted: sec.quoted, billed: sec.billed, count: sec.items.length,
        isEmpty: sec.items.length === 0, collapsed,
      });
      if (collapsed) return;
      if (!sec.hasBands) {
        sec.items.forEach((it) => out.push(it));
        return;
      }
      // Sub-trade bands: a lighter drawer header per sub-trade, each its own
      // collapsible group (key namespaced under the chapter so toggles are
      // independent of same-named bands in other chapters).
      sec.subgroups.forEach((sg) => {
        const subKey = `${sec.key}::sub::${sg.label}`;
        const subCollapsed = collapsedKeys.has(subKey);
        out.push({
          __subgroup: true, id: `__subgroup__${subKey}`, key: subKey,
          label: sg.label || '(no sub-trade)',
          quoted: sg.quoted, billed: sg.billed, count: sg.items.length,
          collapsed: subCollapsed,
        });
        if (!subCollapsed) sg.items.forEach((it) => out.push(it));
      });
    });
    return out;
  }, [sections, collapsedKeys, rowData]);

  // Collapse/expand-all wiring. Only non-empty sections are "collapsible".
  const collapsibleKeys = useMemo(
    () => (sections ? sections.filter((s) => s.items.length > 0).map((s) => s.key) : []),
    [sections]
  );
  const anyCollapsible = collapsibleKeys.length > 0;
  const allCollapsed = anyCollapsible && collapsibleKeys.every((k) => collapsedKeys.has(k));
  const expandAll = useCallback(() => setCollapsedKeys(new Set()), []);
  const collapseAll = useCallback(() => setCollapsedKeys(new Set(collapsibleKeys)), [collapsibleKeys]);

  // Surface the collapse controls to the parent toolbar (the expand/collapse-all
  // button lives next to Refresh).
  useEffect(() => {
    if (!onCollapseControls) return;
    onCollapseControls({ grouped: effectiveGrouped, anyCollapsible, allCollapsed, expandAll, collapseAll });
  }, [onCollapseControls, effectiveGrouped, anyCollapsible, allCollapsed, expandAll, collapseAll]);

  // Keep latest rowData reachable from handleCellValueChanged without making
  // the callback re-create every render (AG Grid would re-bind unnecessarily).
  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;

  const handleCellValueChanged = useCallback(async (e) => {
    const { data, colDef, newValue, oldValue, node } = e;
    if (newValue === oldValue) return;

    let payload;
    if (colDef.field === 'quantity') {
      const n = Number(newValue);
      if (isNaN(n) || n < 0) {
        node.setDataValue('quantity', oldValue);
        return;
      }
      payload = { quantity: n };
    } else if (colDef.field === 'projectZoneId') {
      payload = { projectZoneId: newValue || null };
    } else if (colDef.field === 'shortDescription') {
      const trimmed = (newValue || '').trim();
      const matchesLibrary = trimmed === (data.versionDescription || '').trim();
      payload = { descriptionOverride: matchesLibrary || !trimmed ? null : trimmed };
    } else if (colDef.field === 'sequenceOrder') {
      const n = Number(newValue);
      if (isNaN(n)) {
        node.setDataValue('sequenceOrder', oldValue);
        return;
      }
      payload = { sequenceOrder: n };
    } else if (colDef.field === 'measureBasis') {
      payload = { measureBasis: newValue || null };
    } else if (colDef.field === 'scopePartyId') {
      payload = { scopePartyId: newValue || null };
    } else if (colDef.field === 'optionGroup') {
      // Typing an Option label is the only way to create / join a group.
      // Auto-pick is_selected so the "exactly one selected per group"
      // invariant holds without the user having to think about it:
      //   - leaving the cell empty → ordinary line, always included.
      //   - joining a group that already has a selected sibling → become an
      //     alternate (is_selected = false); the user can flip later with the
      //     "Use this option" button on the row.
      //   - joining a brand-new (or all-unselected) group → become the
      //     selected one (is_selected = true).
      const trimmed = String(newValue || '').trim() || null;
      payload = { optionGroup: trimmed };
      if (!trimmed) {
        payload.isSelected = true;
      } else {
        const hasSelectedSibling = rowDataRef.current.some(
          (r) => r.id !== data.id && r.optionGroup === trimmed && r.isSelected);
        payload.isSelected = !hasSelectedSibling;
      }
    } else if (colDef.field === 'rateQuoted' || colDef.field === 'rateBilled') {
      if (newValue === '' || newValue === null || newValue === undefined) {
        payload = { [colDef.field]: null };
      } else {
        const n = Number(newValue);
        if (isNaN(n) || n < 0) { node.setDataValue(colDef.field, oldValue); return; }
        payload = { [colDef.field]: n };
      }
    } else if (['supplyRateQuoted', 'installRateQuoted', 'supplyRateBilled', 'installRateBilled'].includes(colDef.field)) {
      // Editing a component recomputes the combined rate for that basis.
      const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
      const edited = toNum(newValue);
      if (edited !== null && (isNaN(edited) || edited < 0)) { node.setDataValue(colDef.field, oldValue); return; }
      const isBilled = colDef.field.endsWith('Billed');
      const sf = isBilled ? 'supplyRateBilled' : 'supplyRateQuoted';
      const inf = isBilled ? 'installRateBilled' : 'installRateQuoted';
      const supply = colDef.field === sf ? edited : toNum(data[sf]);
      const install = colDef.field === inf ? edited : toNum(data[inf]);
      payload = { [colDef.field]: edited, [isBilled ? 'rateBilled' : 'rateQuoted']: (supply || 0) + (install || 0) };
    } else {
      return;
    }

    const result = await onUpdate(data.id, payload);
    if (!result.success) {
      node.setDataValue(colDef.field, oldValue);
      // eslint-disable-next-line no-alert
      window.alert(result.error || 'Failed to save change.');
    }
  }, [onUpdate]);

  // When pasting from Excel into a numeric column, strip currency symbols
  // and thousands separators so the cell receives a clean number.
  const processDataFromClipboard = useCallback((params) => params.data, []);
  const processCellFromClipboard = useCallback((params) => {
    const field = params.column.getColDef().field;
    if (['quantity', 'sequenceOrder', 'rateQuoted', 'rateBilled', 'supplyRateQuoted', 'installRateQuoted', 'supplyRateBilled', 'installRateBilled'].includes(field)) {
      const n = parsePastedNumber(params.value);
      return n === null ? params.value : n;
    }
    if (field === 'projectZoneId') {
      // Accept either the zone id (uuid), the label, or an empty string
      const v = String(params.value || '').trim();
      if (!v || v === '—') return null;
      return zoneIdByLabel.get(v) || zoneIdByLabel.get(v.replace(/^[—\s]+/, '')) || params.value;
    }
    return params.value;
  }, [zoneIdByLabel]);

  // Format cells for clipboard so users copying back into Excel get sensible
  // values (zone column copies the label, not the uuid).
  const processCellForClipboard = useCallback((params) => {
    const field = params.column.getColDef().field;
    if (field === 'projectZoneId') {
      return zoneLabelById.get(params.value) || '';
    }
    return params.value;
  }, [zoneLabelById]);

  const handleDuplicateSelected = useCallback(async () => {
    if (!gridRef.current || !onDuplicate) return;
    // Group / sub-trade banners are full-width rows, never real lines — drop them.
    const selected = gridRef.current.api.getSelectedRows()
      .filter((r) => r && !r.__group && !r.__subgroup);
    if (selected.length === 0) {
      // eslint-disable-next-line no-alert
      window.alert('Select one or more rows to duplicate — tick the checkbox on the left, then try again.');
      return;
    }
    // A multi-row duplicate gets one restore point; single-row relies on the
    // per-line undo each duplicate already pushes.
    if (selected.length > 1 && onBeforeDuplicate) await onBeforeDuplicate();
    for (const row of selected) {
      // eslint-disable-next-line no-await-in-loop
      const res = await onDuplicate(row.id);
      if (res && !res.success) {
        // eslint-disable-next-line no-alert
        window.alert(res.error || 'Failed to duplicate a line.');
        break;
      }
    }
  }, [onDuplicate, onBeforeDuplicate]);

  const handleDeleteSelected = useCallback(async () => {
    if (!gridRef.current) return;
    const selected = gridRef.current.api.getSelectedRows();
    if (selected.length === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${selected.length} BOQ line${selected.length === 1 ? '' : 's'}?`)) return;
    if (onBeforeBulk) await onBeforeBulk();
    for (const row of selected) {
      // eslint-disable-next-line no-await-in-loop
      await onDelete(row.id);
    }
  }, [onDelete, onBeforeBulk]);

  const handleExportCsv = useCallback(() => {
    if (!gridRef.current) return;
    gridRef.current.api.exportDataAsCsv({
      fileName: `boq-items-${new Date().toISOString().slice(0, 10)}.csv`,
      shouldRowBeSkipped: (params) => !!(params.node.data?.__group || params.node.data?.__subgroup),
      processCellCallback: (params) => {
        const field = params.column.getColDef().field;
        if (field === 'projectZoneId') return zoneLabelById.get(params.value) || '';
        return params.value;
      },
    });
  }, [zoneLabelById]);

  const columnDefs = useMemo(() => {
    const base = [
    {
      field: 'fullCode',
      headerName: 'Code',
      width: 140,
      pinned: 'left',
      // Tint the code cell by pricing basis (library material calc vs SD block
      // estimate) — a faint, non-semantic fill that flags the line's basis
      // without colouring the whole row.
      cellStyle: (p) => {
        const kind = LINE_KIND[p.data?.kind];
        return { fontFamily: 'monospace', fontSize: 12, ...(kind ? { background: kind.bg } : null) };
      },
      tooltipValueGetter: (p) => (p.data?.kind === 'sd'
        ? 'SD block estimate — lump sum or floor-area rate'
        : (p.data?.kind === 'library' ? 'Library item — measured material calc' : undefined)),
    },
    {
      field: 'subtrade',
      headerName: 'Sub-trade',
      width: 150,
      hide: !hasSubtrade,
    },
    {
      field: 'optionGroup',
      headerName: 'Option',
      width: 180,
      editable: true,
      cellRenderer: OptionCellRenderer,
      autoHeight: true,
      sortable: !effectiveGrouped,
      filter: true,
      tooltipValueGetter: () => 'Lines sharing the same Option label are alternates within one decision (e.g. "First floor structure"). Only the selected one is totalled.',
      // Sort by group label, with the selected alternate first within a group.
      comparator: (a, b, na, nb) => {
        const al = (a || '').toString(); const bl = (b || '').toString();
        if (al !== bl) return al.localeCompare(bl);
        return (nb?.data?.isSelected ? 1 : 0) - (na?.data?.isSelected ? 1 : 0);
      },
    },
    {
      field: 'shortDescription',
      headerName: 'Description',
      width: PI_COL_W.shortDescription,
      editable: true,
      tooltipValueGetter: (p) =>
        p.data.descriptionOverride
          ? `Override (library: "${p.data.versionDescription}")`
          : 'Library description (edit to override)',
      cellStyle: (p) => p.data.descriptionOverride ? { fontStyle: 'italic' } : null,
    },
    {
      field: 'unit',
      headerName: 'Unit',
      width: 90,
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 120,
      // Lines with measurements derive their quantity from the take-off; only
      // manual lines (no measurements) are editable here.
      editable: (p) => !(p.data.measurementCount > 0),
      type: 'numericColumn',
      valueParser: (p) => Number(p.newValue),
      valueFormatter: (p) => formatQty(p.value),
      cellStyle: (p) => p.data.measurementCount > 0
        ? { color: 'var(--brio-gold, #9F902A)', fontWeight: 600 }
        : null,
      tooltipValueGetter: (p) => p.data.measurementCount > 0
        ? `Derived from ${p.data.measurementCount} measurement(s) — edit in take-off`
        : 'Manual quantity',
    },
    {
      field: 'measureBasis',
      headerName: 'Basis',
      width: 120,
      editable: (p) => !(p.data.lineType && p.data.lineType !== 'measured'),
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: BASIS_VALUES },
      valueFormatter: (p) => (isLumpType(p.data.lineType) ? '—' : (BASIS_LABEL[p.value || ''] || p.value)),
    },
    {
      colId: 'takeoff',
      headerName: 'Take-off',
      width: 120,
      sortable: false,
      filter: false,
      cellRenderer: MeasureCellRenderer,
    },
    {
      colId: 'rate',
      field: rateField,
      headerName: billView === 'billed' ? 'Rate (tender)' : 'Rate (estimate)',
      width: 130,
      editable: !splitActive,
      type: 'numericColumn',
      valueParser: (p) => (p.newValue === '' || p.newValue === null ? null : Number(p.newValue)),
      valueFormatter: (p) => formatMoney(p.value, currency),
      cellStyle: splitActive ? { color: 'var(--brio-text-muted)' } : null,
      tooltipValueGetter: () => (splitActive ? 'Combined rate = supply + install (edit the split columns)' : undefined),
    },
    {
      field: 'supplyRateQuoted',
      headerName: 'Supply rate',
      width: 120,
      hide: !(splitActive && billView === 'quoted'),
      editable: splitActive,
      type: 'numericColumn',
      valueParser: (p) => (p.newValue === '' || p.newValue === null ? null : Number(p.newValue)),
      valueFormatter: (p) => formatMoney(p.value, currency),
    },
    {
      field: 'installRateQuoted',
      headerName: 'Install rate',
      width: 120,
      hide: !(splitActive && billView === 'quoted'),
      editable: splitActive,
      type: 'numericColumn',
      valueParser: (p) => (p.newValue === '' || p.newValue === null ? null : Number(p.newValue)),
      valueFormatter: (p) => formatMoney(p.value, currency),
    },
    {
      field: 'supplyRateBilled',
      headerName: 'Supply rate',
      width: 120,
      hide: !(splitActive && billView === 'billed'),
      editable: splitActive,
      type: 'numericColumn',
      valueParser: (p) => (p.newValue === '' || p.newValue === null ? null : Number(p.newValue)),
      valueFormatter: (p) => formatMoney(p.value, currency),
    },
    {
      field: 'installRateBilled',
      headerName: 'Install rate',
      width: 120,
      hide: !(splitActive && billView === 'billed'),
      editable: splitActive,
      type: 'numericColumn',
      valueParser: (p) => (p.newValue === '' || p.newValue === null ? null : Number(p.newValue)),
      valueFormatter: (p) => formatMoney(p.value, currency),
    },
    {
      colId: 'amount',
      field: amountField,
      headerName: billView === 'billed' ? 'Amount (tender)' : 'Amount (estimate)',
      width: 150,
      editable: false,
      type: 'numericColumn',
      valueFormatter: (p) => formatMoney(p.value, currency),
      cellStyle: { fontWeight: 600 },
    },
    {
      colId: 'rateRef',
      headerName: 'Rate ref',
      width: 90,
      sortable: false,
      filter: false,
      cellRenderer: RatesCellRenderer,
    },
    {
      field: 'projectZoneId',
      headerName: 'Zone',
      width: PI_COL_W.projectZoneId,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: zoneIds },
      valueFormatter: (p) => zoneLabelById.get(p.value) || (p.value ? '?' : '—'),
    },
    {
      field: 'scopePartyId',
      headerName: 'Scope',
      width: 150,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: scopeIds },
      valueFormatter: (p) => scopeNameById.get(p.value) || (p.value ? '?' : '—'),
      tooltipValueGetter: () => 'Design / spec responsibility (↗ = external party). Manage via the Scope button.',
      hide: scopeParties.length === 0,
    },
    {
      field: 'sequenceOrder',
      headerName: 'Order',
      width: 90,
      editable: true,
      type: 'numericColumn',
      hide: true,
    },
    ];
    return decorateColumns(base);
  }, [zoneIds, zoneLabelById, scopeIds, scopeNameById, scopeParties.length, rateField, amountField, billView, currency, splitActive, hasSubtrade, effectiveGrouped, decorateColumns]);

  // Fade unselected alternates so it's obvious they don't contribute; trade
  // section headers get the same gold-faint band the Summary's section rows use.
  // (Pricing-basis colour lives on the Code cell only — see the fullCode col.)
  const getRowStyle = useCallback((p) => {
    if (p.data && p.data.__group) {
      return { background: 'var(--brio-gold-faint, #f7f1d4)', borderTop: '1px solid var(--brio-border, #ddd)' };
    }
    if (p.data && p.data.__subgroup) {
      return { background: 'var(--brio-surface-alt, #f6f4ee)', borderTop: '1px solid var(--brio-border-subtle, #eee)' };
    }
    if (p.data && p.data.optionGroup && !p.data.isSelected) {
      return { color: 'var(--brio-text-muted, #888)', fontStyle: 'italic', background: '#fafafa' };
    }
    return undefined;
  }, []);

  const isFullWidthRow = useCallback((p) => !!(p.rowNode.data?.__group || p.rowNode.data?.__subgroup), []);

  // AG Grid keys the Option cell on field='optionGroup', so it doesn't repaint
  // when only is_selected flips (the cell value didn't change). Force-refresh
  // the column whenever any selection toggles to keep the badge/button in sync.
  const optionSelectionKey = useMemo(
    () => rowData.map((r) => `${r.id}:${r.isSelected ? 1 : 0}`).join('|'),
    [rowData]
  );
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells({ columns: ['optionGroup'], force: true });
    }
  }, [optionSelectionKey]);

  const defaultColDef = useMemo(() => ({
    sortable: !effectiveGrouped,           // grouping defines row order; no sort
    filter: false,
    resizable: true,
    suppressMovable: false,
    enableCellChangeFlash: true,
    minWidth: COLLAPSED_W,                 // allow collapsing to a sliver
    headerComponent: BoqGridHeader,
  }), [effectiveGrouped]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Filter rows…"
          value={quickFilter}
          onChange={(e) => setQuickFilter(e.target.value)}
          style={{ flex: '0 0 220px', padding: '0.3rem 0.55rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, fontSize: '0.85rem' }}
        />
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, overflow: 'hidden' }}>
            {[['quoted', 'Estimate'], ['billed', 'Tender']].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setBillView(v)}
                style={{
                  fontSize: '0.78rem', padding: '0.3rem 0.7rem', border: 'none', cursor: 'pointer',
                  background: billView === v ? 'var(--brio-gold, #9F902A)' : 'transparent',
                  color: billView === v ? '#fff' : 'var(--brio-text, #333)',
                }}>
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setGroupByTrade((v) => !v)}
            title="Group the bill into trade sections with subtotals (like the Summary). Turn off for a flat, sortable list."
            style={{
              fontSize: '0.78rem', padding: '0.3rem 0.7rem', borderRadius: 4, cursor: 'pointer',
              border: '1px solid var(--brio-border, #ccc)',
              background: groupByTrade ? 'var(--brio-gold, #9F902A)' : 'transparent',
              color: groupByTrade ? '#fff' : 'var(--brio-text, #333)',
            }}
          >
            Group by trade
          </button>
          <button
            type="button"
            onClick={() => setShowSplit((v) => !v)}
            title="Show the supply / installation rate breakdown (estimate or tender)"
            style={{
              fontSize: '0.78rem', padding: '0.3rem 0.7rem', borderRadius: 4, cursor: 'pointer',
              border: '1px solid var(--brio-border, #ccc)',
              background: showSplit ? 'var(--brio-gold, #9F902A)' : 'transparent',
              color: showSplit ? '#fff' : 'var(--brio-text, #333)',
            }}
          >
            Supply / install split
          </button>
          <ToolbarIcon onClick={resetLayout} tooltip="Reset column layout (widths, order, collapsed &amp; frozen)">
            <RotateCcw size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={handleExportCsv} tooltip="Export CSV">
            <Download size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={handleDuplicateSelected} tooltip="Duplicate selected rows">
            <Copy size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={handleDeleteSelected} danger tooltip="Delete selected rows">
            <Trash2 size={15} />
          </ToolbarIcon>
        </div>
      </div>
      {(hasLibrary || hasSd) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.1rem', flexWrap: 'wrap', margin: '0 0.1rem 0.45rem', fontSize: '0.74rem', color: 'var(--brio-text-muted)' }}>
          <span style={{ fontWeight: 600, color: 'var(--brio-text, #333)' }}>Code colour by pricing basis:</span>
          {hasLibrary && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 14, height: 14, borderRadius: 2, background: LINE_KIND.library.bg, border: '1px solid var(--brio-border, #ddd)' }} />
              {LINE_KIND.library.label}
            </span>
          )}
          {hasSd && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 14, height: 14, borderRadius: 2, background: LINE_KIND.sd.bg, border: '1px solid var(--brio-border, #ddd)' }} />
              {LINE_KIND.sd.label}
            </span>
          )}
        </div>
      )}
      <StickyScrollbar targetRef={gridBoxRef} selector=".ag-body-horizontal-scroll-viewport" />
      <div ref={gridBoxRef} style={{ width: '100%', height: gridHeight }}>
        <AgGridReact
          ref={gridRef}
          theme={brioGridTheme}
          rowData={displayRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          context={gridContext}
          getRowStyle={getRowStyle}
          isFullWidthRow={isFullWidthRow}
          fullWidthCellRenderer={GroupBandRowRenderer}
          onGridReady={(p) => applySavedOrder(p.api)}
          onColumnResized={onColumnResized}
          onColumnMoved={onColumnMoved}
          rowSelection={QS_ROW_SELECTION}
          selectionColumnDef={QS_SELECTION_COL_DEF}
          cellSelection
          onCellValueChanged={handleCellValueChanged}
          processCellFromClipboard={processCellFromClipboard}
          processCellForClipboard={processCellForClipboard}
          processDataFromClipboard={processDataFromClipboard}
          singleClickEdit={false}
          stopEditingWhenCellsLoseFocus
          enterNavigatesVertically
          enterNavigatesVerticallyAfterEdit
          animateRows={false}
          getRowId={(p) => p.data.id}
          quickFilterText={quickFilter}
        />
      </div>
      {/* Drag to grow / shrink the table; double-click to reset to default. */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize table height (drag; double-click to reset)"
        title="Drag to resize · double-click to reset"
        onPointerDown={onResizeStart}
        onDoubleClick={resetGridHeight}
        style={{
          height: 12, marginTop: 3, cursor: 'ns-resize', touchAction: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 4, background: 'var(--brio-surface-2, #f6f5ef)',
          border: '1px solid var(--brio-border, #e4e2d6)',
        }}
      >
        <div style={{ width: 44, height: 4, borderRadius: 2, background: 'var(--brio-border, #c7c4b4)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: '1.5rem', padding: '0.6rem 0.3rem 0', borderTop: '2px solid var(--brio-border, #ddd)', marginTop: '0.3rem' }}>
        {hasOptions && alternateQuoted > 0 && (
          <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.78rem', fontStyle: 'italic' }}>
            Alternates not selected ({formatMoney(alternateQuoted, currency)}) excluded from totals
          </span>
        )}
        <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.85rem' }}>
          Estimate total <strong style={{ color: 'var(--brio-text, #333)', fontSize: '1rem', marginLeft: 4 }}>{formatMoney(totalQuoted, currency)}</strong>
        </span>
        <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.85rem' }}>
          Tender total <strong style={{ color: 'var(--brio-text, #333)', fontSize: '1rem', marginLeft: 4 }}>{formatMoney(totalBilled, currency)}</strong>
        </span>
      </div>
      <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: 'var(--brio-text-muted)' }}>
        Tab / Enter to move · Ctrl/Cmd+C/V to copy-paste with Excel · on iPad use the toolbar's paste &amp; undo. Amount = quantity × rate. Type the same label into the <strong>Option</strong> column on two or more rows to group them as alternates — only the selected one is totalled. The bill is organised into <strong>trade sections</strong> (like the Summary); turn off <strong>Group by trade</strong> for a flat, sortable list when pasting across trades. The <strong>Code</strong> cell is tinted by <strong>pricing basis</strong> — library items (grey) are measured material calcs; SD block estimates (gold) are lump / area-rate figures.
      </div>
    </div>
  );
}

export default ProjectItemsGrid;
